import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useToast } from '../hooks/useToast';
import teamService from '../services/teamService';
import type {
  Team,
  TeamMember,
  TeamSettings,
  CreateTeamInput,
  UpdateTeamInput,
  AddTeamMemberInput,
  UpdateTeamMemberInput,
  UpdateTeamSettingsInput,
} from '../types/team';

interface TeamContextType {
  // State
  teams: Team[];
  currentTeam: Team | null;
  isLoading: boolean;
  error: string | null;

  // Team operations
  refreshTeams: () => Promise<void>;
  getTeamById: (teamId: number) => Promise<Team>;
  createTeam: (data: CreateTeamInput) => Promise<Team>;
  updateTeam: (teamId: number, data: UpdateTeamInput) => Promise<Team>;
  deleteTeam: (teamId: number) => Promise<void>;
  setCurrentTeam: (team: Team | null) => void;

  // Member operations
  getTeamMembers: (teamId: number) => Promise<TeamMember[]>;
  addMember: (teamId: number, data: AddTeamMemberInput) => Promise<TeamMember>;
  updateMemberRole: (teamId: number, userId: number, data: UpdateTeamMemberInput) => Promise<TeamMember>;
  removeMember: (teamId: number, userId: number) => Promise<void>;

  // Settings operations
  getTeamSettings: (teamId: number) => Promise<TeamSettings>;
  updateTeamSettings: (teamId: number, data: UpdateTeamSettingsInput) => Promise<TeamSettings>;

  // Utility
  clearError: () => void;
}

const TeamContext = createContext<TeamContextType>({
  teams: [],
  currentTeam: null,
  isLoading: false,
  error: null,
  refreshTeams: async () => {},
  getTeamById: async () => ({} as Team),
  createTeam: async () => ({} as Team),
  updateTeam: async () => ({} as Team),
  deleteTeam: async () => {},
  setCurrentTeam: () => {},
  getTeamMembers: async () => [],
  addMember: async () => ({} as TeamMember),
  updateMemberRole: async () => ({} as TeamMember),
  removeMember: async () => {},
  getTeamSettings: async () => ({} as TeamSettings),
  updateTeamSettings: async () => ({} as TeamSettings),
  clearError: () => {},
});

export const useTeam = () => useContext(TeamContext);

interface TeamProviderProps {
  children: ReactNode;
}

export const TeamProvider: React.FC<TeamProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const toast = useToast();

  const [teams, setTeams] = useState<Team[]>([]);
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  /**
   * Fetch teams the user is a member of
   *
   * NOTE: This will skip fetching if the user has no organization yet (e.g., during onboarding).
   * Uses the /my-teams endpoint which returns only teams the user belongs to,
   * ensuring team members (not just admins) can see their teams.
   */
  const refreshTeams = async () => {
    if (!isAuthenticated || !user) {
      return;
    }

    // Skip fetching teams if user has no organization yet (during onboarding)
    // This prevents 403 errors when user hasn't completed onboarding
    if (!user.organizationId) {
      setTeams([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const fetchedTeams = await teamService.getMyTeams();
      setTeams(fetchedTeams);
    } catch (err: any) {
      // 403 means the user's plan doesn't include team-management.
      // Treat this as "no teams" rather than an error — the user simply
      // doesn't have the feature, so there's nothing to display.
      if (err.response?.status === 403) {
        setTeams([]);
        return;
      }
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch teams';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get a specific team by ID
   */
  const getTeamById = async (teamId: number): Promise<Team> => {
    try {
      setIsLoading(true);
      setError(null);
      const team = await teamService.getTeamById(teamId);
      return team;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch team';
      setError(errorMessage);
      toast.error('teams:errors.fetchFailed', 'Failed to fetch team');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Create a new team
   */
  const createTeam = async (data: CreateTeamInput): Promise<Team> => {
    try {
      setIsLoading(true);
      setError(null);

      const newTeam = await teamService.createTeam(data);

      // Refresh teams to include the new team
      await refreshTeams();

      toast.success('teams:messages.createSuccess', 'Team created successfully');
      return newTeam;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create team';
      setError(errorMessage);
      toast.error('teams:errors.createFailed', 'Failed to create team');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update a team
   */
  const updateTeam = async (teamId: number, data: UpdateTeamInput): Promise<Team> => {
    try {
      setIsLoading(true);
      setError(null);

      const updatedTeam = await teamService.updateTeam(teamId, data);

      // Update teams list
      setTeams(prevTeams =>
        prevTeams.map(team => (team.id === teamId ? updatedTeam : team))
      );

      // Update current team if it's the one being updated
      if (currentTeam && currentTeam.id === teamId) {
        setCurrentTeam(updatedTeam);
      }

      toast.success('teams:messages.updateSuccess', 'Team updated successfully');
      return updatedTeam;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update team';
      setError(errorMessage);
      toast.error('teams:errors.updateFailed', 'Failed to update team');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a team
   */
  const deleteTeam = async (teamId: number): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      await teamService.deleteTeam(teamId);

      // Remove team from list
      setTeams(prevTeams => prevTeams.filter(team => team.id !== teamId));

      // Clear current team if it's the one being deleted
      if (currentTeam && currentTeam.id === teamId) {
        setCurrentTeam(null);
      }

      toast.success('teams:messages.deleteSuccess', 'Team deleted successfully');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete team';
      setError(errorMessage);
      toast.error('teams:errors.deleteFailed', 'Failed to delete team');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get team members
   */
  const getTeamMembers = async (teamId: number): Promise<TeamMember[]> => {
    try {
      setError(null);
      const members = await teamService.getTeamMembers(teamId);
      return members;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch team members';
      setError(errorMessage);
      toast.error('teams:errors.fetchMembersFailed', 'Failed to fetch team members');
      throw err;
    }
  };

  /**
   * Add a member to the team
   */
  const addMember = async (teamId: number, data: AddTeamMemberInput): Promise<TeamMember> => {
    try {
      setError(null);
      const member = await teamService.addTeamMember(teamId, data);

      // Update team member count
      setTeams(prevTeams =>
        prevTeams.map(team =>
          team.id === teamId
            ? { ...team, memberCount: (team.memberCount || 0) + 1 }
            : team
        )
      );

      toast.success('teams:messages.addMemberSuccess', 'Member added successfully');
      return member;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to add member';
      setError(errorMessage);
      toast.error('teams:errors.addMemberFailed', 'Failed to add member');
      throw err;
    }
  };

  /**
   * Update a team member's role
   */
  const updateMemberRole = async (
    teamId: number,
    userId: number,
    data: UpdateTeamMemberInput
  ): Promise<TeamMember> => {
    try {
      setError(null);
      const member = await teamService.updateTeamMember(teamId, userId, data);
      return member;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update member role';
      setError(errorMessage);
      toast.error('teams:errors.updateMemberFailed', 'Failed to update member role');
      throw err;
    }
  };

  /**
   * Remove a member from the team
   */
  const removeMember = async (teamId: number, userId: number): Promise<void> => {
    try {
      setError(null);
      await teamService.removeTeamMember(teamId, userId);

      // Update team member count
      setTeams(prevTeams =>
        prevTeams.map(team =>
          team.id === teamId
            ? { ...team, memberCount: Math.max((team.memberCount || 1) - 1, 0) }
            : team
        )
      );

      toast.success('teams:messages.removeMemberSuccess', 'Member removed successfully');
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to remove member';
      setError(errorMessage);
      toast.error('teams:errors.removeMemberFailed', 'Failed to remove member');
      throw err;
    }
  };

  /**
   * Get team settings
   */
  const getTeamSettings = async (teamId: number): Promise<TeamSettings> => {
    try {
      setError(null);
      const settings = await teamService.getTeamSettings(teamId);
      return settings;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch team settings';
      setError(errorMessage);
      toast.error('teams:errors.fetchSettingsFailed', 'Failed to fetch team settings');
      throw err;
    }
  };

  /**
   * Update team settings
   */
  const updateTeamSettings = async (
    teamId: number,
    data: UpdateTeamSettingsInput
  ): Promise<TeamSettings> => {
    try {
      setError(null);
      const settings = await teamService.updateTeamSettings(teamId, data);
      toast.success('teams:messages.updateSettingsSuccess', 'Settings updated successfully');
      return settings;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update settings';
      setError(errorMessage);
      toast.error('teams:errors.updateSettingsFailed', 'Failed to update settings');
      throw err;
    }
  };

  // Fetch teams on mount when user is authenticated and has an organization
  // Skip during onboarding when user doesn't have an organization yet
  useEffect(() => {
    if (isAuthenticated && user) {
      // Only fetch teams if user has an organization
      if (user.organizationId) {
        refreshTeams();
      } else {
        // User is in onboarding - don't fetch teams
        setTeams([]);
        setCurrentTeam(null);
      }
    } else {
      setTeams([]);
      setCurrentTeam(null);
    }
  }, [isAuthenticated, user?.id, user?.organizationId]);

  return (
    <TeamContext.Provider
      value={{
        teams,
        currentTeam,
        isLoading,
        error,
        refreshTeams,
        getTeamById,
        createTeam,
        updateTeam,
        deleteTeam,
        setCurrentTeam,
        getTeamMembers,
        addMember,
        updateMemberRole,
        removeMember,
        getTeamSettings,
        updateTeamSettings,
        clearError,
      }}
    >
      {children}
    </TeamContext.Provider>
  );
};

export default TeamContext;
